import { useEffect, useState } from "react";
import { Box, Paper, TextField, useTheme } from "@mui/material";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  IntegrationType,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { Controller, useForm } from "react-hook-form";
import { Space } from "~/lib/types";
import { TicketIntegration } from "~/pages/tickets/components/TicketSettings/types";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { Flex } from "~/components/Flex";
import {
  helperTextStyles,
  ValidationMessage,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { GitlabIssueFormInput } from "~/pages/integrations/hosted-integrations/forms/gitlab-issues/GitlabIssuesIntegrationForm";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CaseSettingsIntegrationProps = {
  integration: TicketIntegration;
  space: Space;
  showAutoCreateSection: boolean;
};

export const TicketSettingsGitlabIntegration = ({
  integration,
  space,
  showAutoCreateSection,
}: CaseSettingsIntegrationProps) => {
  const theme = useTheme();
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getFieldState,
    formState: { errors },
  } = useForm<GitlabIssueFormInput>({
    defaultValues: {
      defaultProject: "",
      autoCloseTickets: false,
      autoCreateTickets: false,
    },
    mode: "onChange",
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const onSubmit = async (data: GitlabIssueFormInput) => {
    const gitlabTicketingConfigurationOptions = {
      baseUrl: data.baseUrl || "",
      defaultProject: data.defaultProject || "",
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
      token: "",
    };

    await updateIntegration({
      variables: {
        input: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
          type: ClientIntegrationType.TicketSystemGitlab,
          configurationOptions: {
            gitlabTicketingConfigurationOptions,
          },
        },
      },
    });
  };

  const [watchAutoCreate, watchAutoClose] = watch([
    "autoCreateTickets",
    "autoCloseTickets",
  ]);

  useEffect(() => {
    isInitialized && handleSubmit(onSubmit)();
  }, [watchAutoClose, watchAutoCreate]);

  const handleTextFieldBlur = () => {
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    const configOptions = integration?.configurationOptions;
    if (configOptions?.__typename !== "GitlabTicketingConfigurationOptions")
      return;

    reset({
      baseUrl: configOptions.gitlabBaseUrl || "",
      defaultProject: configOptions.gitlabDefaultProject || "",
      autoCloseTickets: configOptions.autoCloseTickets,
      autoCreateTickets: configOptions.autoCreateTickets,
    });
    setIsInitialized(true);
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <JiraAutoConfigurationSection
          control={control}
          ticketType={IntegrationType.TicketSystemGitlab}
          showAutoCreateSection={showAutoCreateSection}
          autoCreateSubField={
            <Box
              component={motion.div}
              initial={false}
              transition={{
                duration: 0.35,
                staggerChildren: 0.25,
              }}
              sx={{ overflow: "hidden" }}
            >
              <SubField
                configuration="Select a drift group and project"
                caption="Specify the GitLab group and project in which to create an issue for each new drift case."
                disabled={!watchAutoCreate}
                field={
                  <Flex flex="1 0 auto" sx={{ pl: 2 }} alignItems="flex-end">
                    <Controller
                      name="defaultProject"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          onBlur={handleTextFieldBlur}
                          fullWidth
                          disabled={!watchAutoCreate}
                          sx={{
                            background: theme.palette.code.background,
                            borderRadius: 1,
                            color: "text.primary",
                            ...helperTextStyles,
                          }}
                          placeholder={"your-group/your-project ..."}
                          error={
                            getFieldState("defaultProject").isTouched &&
                            Boolean(errors.defaultProject)
                          }
                          helperText={
                            Boolean(errors.defaultProject) &&
                            getFieldState("defaultProject").isTouched && (
                              <ValidationMessage
                                error={errors.defaultProject}
                              />
                            )
                          }
                        />
                      )}
                    />
                  </Flex>
                }
              />
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};
